/// <amd-module name="Feed/Legacy/Protocols/Maven/MavenConnectToFeedPanel" />
import * as React from "react";

import { Dropdown, IDropdownOption } from "OfficeFabric/Dropdown";

import * as Utils_String from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageService } from "VSS/Platform/Page";
import * as Url from "VSS/Platform/Util/Url";

import { CiConstants, FwLinks, TfsServiceInstanceId } from "Feed/Common/Constants/Constants";
import { FeedView } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { ExternalLink } from "Feed/Legacy/Components/ExternalLink";
import { LabelWithExternalLink } from "Feed/Legacy/Components/LabelWithExternalLink";
import { PackageMessagePanel } from "Feed/Legacy/Components/PackageMessagePanel";
import { HubWebPageDataService } from "Feed/Legacy/DataServices/WebPageDataService";
import { CustomerIntelligenceHelper } from "Feed/Legacy/Helpers/CustomerIntelligenceHelper";
import { isUserAnonymousOrPublic } from "Feed/Legacy/Helpers/PermissionHelper";
import { IConnectToFeedProps } from "Feed/Legacy/Protocols/Common/IConnectToFeedProps";
import { CopyableTextField, ICopyableTextFieldTelemetry } from "Feed/Legacy/Protocols/Components/CopyableTextField";
import { MavenCiConstants, MavenExternalLinks } from "Feed/Legacy/Protocols/Maven/Constants/MavenConstants";
import { IMavenRestApiUrlGeneratorService } from "Feed/Legacy/Protocols/Maven/MavenRestApiUrlGeneratorService";
import { HubAction } from "Feed/Legacy/Types/IHubState";
import { getServiceRootPath } from "Helpers/UrlHelper";

export interface IMavenConnectToFeedPanelProps extends IConnectToFeedProps {
    feedViews: FeedView[];
}

export interface IMavenConnectToFeedPanelState {
    endpointUrl: string;
    isAuthTokenLoading?: boolean;
    repositoryId: string;
    selectedView: FeedView;
}

export class MavenConnectToFeedPanel extends React.Component<
    IMavenConnectToFeedPanelProps,
    IMavenConnectToFeedPanelState
> {
    private _generatedCredentials: CopyableTextField;
    private showProjectInUrl: boolean;
    private showMemberDetails: boolean;

    private textfieldIds = ["maven-textfield-label1", "maven-textfield-label2", "maven-textfield-label3"];

    constructor(props: IMavenConnectToFeedPanelProps) {
        super(props);

        const { name, view } = { ...props.feed };
        this.showProjectInUrl = props.feed && props.feed.project != null;
        const feedId = view ? `${name}@${view.name}` : name;
        const endpointUrl = this.UrlGeneratorService.getEndpointUrl(feedId, this.showProjectInUrl);
        this.showMemberDetails = isUserAnonymousOrPublic(this.props.pageContext, this.props.feed) == false;

        this.state = {
            repositoryId: this._createRepositoryId(),
            // Decode endpoint URL so that it shows unencoded in Maven XML configuration snippets. Otherwise, copy-pasting the encoded URLs will cause
            // Maven client to re-encode the encoded URLs once more, effectively altering the feed URL.
            endpointUrl: decodeURI(endpointUrl),
            selectedView: view || null
        } as IMavenConnectToFeedPanelState;

        CustomerIntelligenceHelper.publishEvent(this.props.pageContext, CiConstants.ConnectToFeed, {
            Action: CiConstants.ConnectToFeedTabChanged,
            Protocol: MavenCiConstants.ProtocolName,
            Context: HubAction[this.props.hubAction]
        });
    }

    private get UrlGeneratorService(): IMavenRestApiUrlGeneratorService {
        return this.props.pageContext.getService<IMavenRestApiUrlGeneratorService>("IMavenRestApiUrlGeneratorService");
    }

    get WebPageDataService(): HubWebPageDataService {
        return this.props.pageContext.getService<HubWebPageDataService>("IFeedWebPageDataService");
    }

    public render(): JSX.Element {
        const viewOptions: IDropdownOption[] = [];
        viewOptions.push({
            key: PackageResources.FeedViewDropdown_AllViews_Key,
            text: PackageResources.FeedViewDropdown_ConnectToFeed_AllViews_Name
        } as IDropdownOption);

        if (this.props.feedViews) {
            this.props.feedViews.forEach(view => {
                viewOptions.push({
                    key: view.id,
                    text: "@" + view.name
                } as IDropdownOption);
            });
        }

        return (
            <div className="maven-connect-to-feed-panel connect-to-feed-panel">
                {this.state.endpointUrl ? (
                    <div className="connect-to-feed-section">
                        <div className="connect-to-feed-section-title" role="heading" aria-level={2}>
                            {PackageResources.MavenConnectControl_ConsumeHeadingText}
                        </div>
                        <div className="connect-to-feed-views-dropdown">
                            <LabelWithExternalLink
                                pageContext={this.props.pageContext}
                                labelText={PackageResources.FeedViewDropdown_ConnectToFeed_Label}
                                linkText={PackageResources.FeedViewDropdown_ConnectToFeed_Label_Link}
                                href={FwLinks.ConnectToFeedViewWhatsThis}
                                ciContext={MavenCiConstants.MavenConnectToFeedPanel}
                            />
                            <Dropdown
                                className="views-dropdown"
                                ariaLabel={PackageResources.FeedViewDropdown_ConnectToFeed_Label}
                                options={viewOptions}
                                onChanged={this._onViewDropdownChanged}
                                defaultSelectedKey={
                                    this.state.selectedView
                                        ? this.state.selectedView.id
                                        : PackageResources.FeedViewDropdown_AllViews_Key
                                }
                            />
                        </div>
                        {this.showMemberDetails && this.renderGenerateCredentials()}
                        <div className="maven-subheading" id={this.textfieldIds[1]}>
                            <FormatComponent format={PackageResources.MavenConnectControl_PomXmlTitle}>
                                <span className="xml-tag-text">{"<repositories>"}</span>
                                <span className="xml-tag-text">{"<distributionManagement>"}</span>
                            </FormatComponent>
                        </div>
                        <CopyableTextField
                            pageContext={this.props.pageContext}
                            ariaDescribedByIdForLabelText={this.textfieldIds[1]}
                            text={this._createRepositoryXmlTag()}
                            buttonAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Maven_Consume_Copy}
                            textFieldAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Maven_Consume}
                            telemetryProperties={
                                {
                                    commandName: CiConstants.CopyCommandAddSource,
                                    feedName: this.props.feed.name,
                                    protocol: MavenCiConstants.ProtocolName
                                } as ICopyableTextFieldTelemetry
                            }
                        />
                        <ExternalLink
                            pageContext={this.props.pageContext}
                            className="connect-to-feed-link"
                            href={MavenExternalLinks.PomRepositoriesReference}
                            ciContext={MavenCiConstants.MavenConnectToFeedPanel}
                        >
                            {PackageResources.MavenConnectControl_ExternalLinkForPOM}
                        </ExternalLink>
                        {this.showMemberDetails && this.renderPublishSteps()}
                    </div>
                ) : (
                    <PackageMessagePanel message={PackageResources.ConnectControl_EndpointNotAvailable} />
                )}
            </div>
        );
    }

    private renderGenerateCredentials() {
        return (
            <div>
                <div className="maven-subheading" id={this.textfieldIds[0]}>
                    <FormatComponent format={PackageResources.MavenConnectControl_CredentialsTitle}>
                        <span className="xml-tag-text">{"<servers>"}</span>
                    </FormatComponent>
                </div>
                <div>
                    <CopyableTextField
                        pageContext={this.props.pageContext}
                        ref={self => (this._generatedCredentials = self)}
                        ariaDescribedByIdForLabelText={this.textfieldIds[0]}
                        text={this._createServerXmlTag()}
                        buttonAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Maven_Token_Copy}
                        textFieldAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Maven_Token}
                        telemetryProperties={
                            {
                                commandName: CiConstants.CopyCommandAuthToken,
                                feedName: this.props.feed.name,
                                protocol: MavenCiConstants.ProtocolName
                            } as ICopyableTextFieldTelemetry
                        }
                    />
                    <span className="warning-text">{PackageResources.MavenConnectControl_WarningText}</span>
                </div>
                <ExternalLink
                    pageContext={this.props.pageContext}
                    className="connect-to-feed-link"
                    href={MavenExternalLinks.SettingsServerReference}
                    ciContext={MavenCiConstants.MavenConnectToFeedPanel}
                >
                    {PackageResources.MavenConnectControl_ExternalLinkForSettings}
                </ExternalLink>
            </div>
        );
    }

    private renderPublishSteps() {
        return (
            <div>
                <div className="connect-to-feed-section-title maven-title" role="heading" aria-level={2}>
                    {PackageResources.MavenConnectControl_PublishHeadingText}
                </div>
                <div className="connect-to-feed-sub-heading" id={this.textfieldIds[2]}>
                    {PackageResources.MavenConnectControl_DeployPluginTitle}
                </div>
                <CopyableTextField
                    pageContext={this.props.pageContext}
                    ariaDescribedByIdForLabelText={this.textfieldIds[2]}
                    text={"mvn deploy"}
                    buttonAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Maven_MavenPlugin_Copy}
                    textFieldAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Maven_MavenPlugin}
                    telemetryProperties={
                        {
                            commandName: CiConstants.CopyCommandAddSource,
                            feedName: this.props.feed.name,
                            protocol: MavenCiConstants.ProtocolName
                        } as ICopyableTextFieldTelemetry
                    }
                />
                <ExternalLink
                    pageContext={this.props.pageContext}
                    className="connect-to-feed-link"
                    href={MavenExternalLinks.DeployPluginReference}
                    ciContext={MavenCiConstants.MavenConnectToFeedPanel}
                >
                    {PackageResources.MavenConnectControl_ExternalLinkForDeployPlugin}
                </ExternalLink>
            </div>
        );
    }

    private _onViewDropdownChanged = (option: IDropdownOption) => {
        let feedId = this.props.feed.name;
        let selectedView: FeedView = null;

        if (option.key !== PackageResources.FeedViewDropdown_AllViews_Key) {
            feedId += option.text;
            selectedView = { id: option.key, name: option.text } as FeedView;
        }

        this.setState({
            endpointUrl: decodeURI(this.UrlGeneratorService.getEndpointUrl(feedId, this.showProjectInUrl)),
            selectedView
        });
    };

    private _createRepositoryXmlTag(): string {
        // using » to indicate where the indentations will be
        const snippet = `<repository>
            »<id>${this.state.repositoryId}</id>
            »<url>${this.state.endpointUrl}</url>
            »<releases>
            »»<enabled>true</enabled>
            »</releases>
            »<snapshots>
            »»<enabled>true</enabled>
            »</snapshots>
            </repository>`;

        return snippet.replace(/  +/g, "").replace(/»/g, "  ");
    }

    private _createServerXmlTag(): string {
        // using » to indicate where the indentations will be
        const snippet = `<server>
            »<id>${this.state.repositoryId}</id>
            »<username>${this.props.feed.name}</username>
            »<!-- ${PackageResources.AuthTokenSecurityWarning} -->
            »<password>[INSERT PAT HERE]</password>
            </server>`;

        return snippet.replace(/  +/g, "").replace(/»/g, "  ");
    }

    private _createRepositoryId(): string {
        const tfsUrl = getServiceRootPath(this.props.pageContext, TfsServiceInstanceId);
        const host = new Url.Uri(tfsUrl).host.trim();
        // collection/org name
        const name = this.props.pageContext.getService<IVssPageService>("IVssPageService").getData().hostName;
        const feed = this.props.feed.name.trim();
        return Utils_String.format("{0}-{1}-{2}", host, name, feed)
            .toLowerCase()
            .replace(/\.|\s/g, "-");
    }
}
